import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { Printer } from "lucide-react";
import { api, TIPOS, PEND_LABELS, SUGERIDOS, etapaInfo, estadoInfo, fmtDate, fmtEur, edadTexto, esRevision, soloRevision } from "@/lib/api";

const Row = ({ k, v }) => (
  <div className="flex gap-2 py-1 border-b border-slate-100 text-sm">
    <span className="w-44 shrink-0 text-slate-500">{k}</span>
    <span className="text-slate-900">{v || "—"}</span>
  </div>
);

const H = ({ children }) => <h2 className="font-[Outfit] text-base font-semibold text-slate-900 mt-6 mb-2 border-b-2 border-slate-800 pb-1">{children}</h2>;

const Treatment = ({ t }) => (
  <div className="mt-4 break-inside-avoid" data-testid={`print-treatment-${t.tipo}`}>
    <div className="font-semibold text-sm">{TIPOS[t.tipo].single} · {estadoInfo(t.estado).label}{t.aparato ? ` · ${t.aparato}` : ""}{t.modalidad ? ` · ${t.modalidad}` : ""}</div>
    <div className="text-xs text-slate-500">Inicio {fmtDate(t.fecha_inicio)} · {t.realizadas}/{t.total} {t.tipo === "bono" ? "sesiones" : "citas"} · Pagado {fmtEur(t.pagado)} · Pendiente {fmtEur(t.pendiente_pago)}</div>
    {t.citas.length > 0 && (
      <table className="w-full text-xs mt-1 border border-slate-200">
        <tbody>{t.citas.map((c) => <tr key={c.id} className="border-b border-slate-100"><td className="px-2 py-0.5 w-6">{c.realizada ? "✔" : "○"}</td><td className="px-2">{c.etiqueta}</td><td className="px-2 w-24">{fmtDate(c.fecha)}</td><td className="px-2 text-slate-500">{c.nota}</td></tr>)}</tbody>
      </table>
    )}
    {t.pagos?.length > 0 && <div className="text-xs mt-1">Pagos: {t.pagos.map((p) => `${fmtDate(p.fecha)} ${fmtEur(p.importe)} (${p.metodo})`).join(" · ")}</div>}
    {t.notas && <div className="text-xs mt-1 text-slate-600">Notas: {t.notas}</div>}
  </div>
);

export default function FichaImprimir() {
  const { id } = useParams();
  const [d, setD] = useState(null);
  const [cues, setCues] = useState([]);
  useEffect(() => {
    api.get(`/patients/${id}`).then((r) => setD(r.data));
    api.get(`/patients/${id}/cuestionarios`).then((r) => setCues(r.data));
  }, [id]);
  if (!d) return <div className="p-8 text-slate-400">Cargando…</div>;
  const p = d.patient;
  const sug = p.tratamientos_sugeridos.map((s) => SUGERIDOS.find((x) => x.id === s)?.label || s).join(", ");
  const claves = cues.flatMap((c) => (c.claves || []).map((i) => ({ ...c.respuestas[i], k: `${c.id}-${i}`, t: c.titulo })).filter((r) => r.pregunta));
  return (
    <div className="bg-white min-h-screen" data-testid="print-page">
      <div className="max-w-[800px] mx-auto p-8">
        <div className="flex justify-between items-start no-print mb-4">
          <span className="text-sm text-slate-500">Vista de impresión. Elige "Guardar como PDF" en la ventana de impresión.</span>
          <button data-testid="print-btn" onClick={() => window.print()} className="inline-flex items-center gap-2 rounded-md bg-[#1E3A8A] text-white px-4 py-2 text-sm"><Printer className="h-4 w-4" />Imprimir / PDF</button>
        </div>
        <div className="flex justify-between items-end border-b border-slate-300 pb-3">
          <div className="flex items-end gap-4">
            <img src="/logo-sv.jpg" alt="Salud y Visión" className="h-16" data-testid="print-logo" />
            <div>
            <div className="text-xs uppercase tracking-wider text-slate-500">Salud y Visión · Ficha de paciente</div>
            <h1 className="font-[Outfit] text-2xl font-bold">{p.nombre}</h1>
            </div>
          </div>
          <div className="text-right text-xs text-slate-500">Etapa: <b className="text-slate-900">{etapaInfo(p.etapa).label}</b><br />Impreso el {fmtDate(new Date().toISOString())}</div>
        </div>
        <H>Datos</H>
        <Row k="Código" v={p.codigo} /><Row k="Fecha nacimiento" v={p.fecha_nacimiento ? `${fmtDate(p.fecha_nacimiento)} (${edadTexto(p)})` : edadTexto(p)} /><Row k="Centro" v={p.centro} /><Row k="Optometrista" v={p.optometrista} /><Row k="Quién remite" v={p.quien_remite} />
        <Row k="Teléfono" v={p.telefono} /><Row k="Email" v={p.email} />
        <Row k="Fecha llamada" v={fmtDate(p.fecha_llamada)} /><Row k="Cita" v={[p.estado_cita, p.fecha_cita && fmtDate(p.fecha_cita)].filter(Boolean).join(" · ")} />
        {p.estado_cita === "ANULADA" && <Row k="Motivo anulación" v={[p.motivo_anulacion, p.motivo_detalle].filter(Boolean).join(" · ")} />}
        {(p.tutor || p.colegio || p.curso) && <><Row k="Padre/madre/tutor" v={p.tutor} /><Row k="Colegio y curso" v={[p.colegio, p.curso].filter(Boolean).join(" · ")} /></>}
        {p.dni && <Row k="DNI" v={p.dni} />}
        {(p.direccion || p.poblacion) && <Row k="Dirección" v={[p.direccion, p.codigo_postal, p.poblacion].filter(Boolean).join(" · ")} />}
        {(p.telefono2 || p.telefono_fijo || p.telefono_trabajo) && <Row k="Otros teléfonos" v={[p.telefono2, p.telefono_fijo && `fijo ${p.telefono_fijo}`, p.telefono_trabajo && `trabajo ${p.telefono_trabajo}`].filter(Boolean).join(" · ")} />}
        {p.profesion && <Row k="Profesión / estudios" v={p.profesion} />}
        {(p.nombre_padre || p.profesion_padre) && <Row k="Padre" v={[p.nombre_padre, p.profesion_padre].filter(Boolean).join(" · ")} />}
        {(p.nombre_madre || p.profesion_madre) && <Row k="Madre" v={[p.nombre_madre, p.profesion_madre].filter(Boolean).join(" · ")} />}
        {p.profesion_tutor && <Row k="Profesión tutor/a" v={p.profesion_tutor} />}
        {p.desde_cuando && <Row k="Desde cuándo" v={p.desde_cuando} />}
        {p.conoce_oc && <Row k="Conoce la O. Comportamental" v={p.conoce_oc} />}
        {(p.motivo_consulta || claves.length > 0) && (
          <div data-testid="print-claves">
            <H>Lo que cuenta la familia</H>
            {p.motivo_consulta && <Row k="Motivo de consulta" v={p.motivo_consulta} />}
            {claves.map((r) => <Row key={r.k} k={r.pregunta} v={r.respuesta} />)}
          </div>
        )}
        <H>Proceso</H>
        <Row k="Examen" v={[p.tipo_examen, p.fecha_ee && fmtDate(p.fecha_ee)].filter(Boolean).join(" · ")} />
        <Row k="Evaluación auditiva" v={p.eval_auditiva} />
        {soloRevision(p) ? <>
          <Row k="Revisión" v={p.momento_rev} /><Row k="Presupuesto de revisión" v={p.ppto_rev} /><Row k="Respuesta revisión" v={p.respuesta_rev} />
          <Row k="Informe" v={[p.tipo_informe, p.estado_informe].filter(Boolean).join(" · ")} /><Row k="Económico" v={p.economico} />
        </> : <>
        {esRevision(p) && <><Row k="Revisión" v={p.momento_rev} /><Row k="Presupuesto de revisión" v={p.ppto_rev} /><Row k="Respuesta revisión" v={p.respuesta_rev} /><Row k="Nuevo tratamiento" v={p.nuevo_tto} /></>}
        <Row k="Necesita PV" v={[p.necesita_pv, p.fecha_pv && fmtDate(p.fecha_pv)].filter(Boolean).join(" · ")} />
        <Row k="Informe" v={[p.tipo_informe, p.estado_informe].filter(Boolean).join(" · ")} />
        <Row k="Tratamientos sugeridos" v={sug} /><Row k="Envío presupuesto" v={p.envio_ppto} />
        <Row k="Respuesta al tratamiento visual" v={p.respuesta} /><Row k="Respuesta oído" v={p.respuesta_oido} />
        <Row k="Hora terapia" v={[p.hora_terapia, p.dia_hora].filter(Boolean).join(" · ")} /><Row k="Económico" v={p.economico} />
        </>}
        <Row k="Pendiente" v={d.pendientes.map((k) => PEND_LABELS[k]).join(", ") || "Nada pendiente"} />
        {p.observaciones && <Row k="Observaciones" v={p.observaciones} />}
        <H>Tratamientos</H>
        {d.treatments.length === 0 ? <p className="text-sm text-slate-500">Sin tratamientos.</p> : d.treatments.map((t) => <Treatment key={t.id} t={t} />)}
        {d.alta && (
          <>
            <H>Protocolo de alta</H>
            <Row k="Fecha alta" v={fmtDate(d.alta.fecha_alta)} />
            {d.alta.revisiones.map((r) => <Row key={r.id} k={`${r.realizada ? "✔" : "○"} ${r.etiqueta}`} v={[fmtDate(r.fecha), r.nota].filter(Boolean).join(" · ")} />)}
            {d.alta.notas && <Row k="Notas" v={d.alta.notas} />}
          </>
        )}
      </div>
    </div>
  );
}
